import { authState } from '../../lib/auth.js';
import { getBackendUrl } from '../../lib/backend.js';
import { BaseComponent } from '../../lib/base-component.js';
import { navigateAfterSignIn } from '../../lib/pending-oauth.js';
import { router } from '../../lib/router.js';
import { css, html } from '../../vendor/lit.min.js';

class SetupView extends BaseComponent {
    static properties = {
        email: { type: String, state: true },
        username: { type: String, state: true },
        avatarPreview: { type: String, state: true },
        avatarFile: { type: Object, state: true },
        error: { type: String, state: true },
        usernameStatus: { type: String, state: true },
        loading: { type: Boolean, state: true },
    };

    static styles = css`
        :host { display: block; }
        .setup-card { max-width: 420px; margin: 0 auto; }
        .avatar-upload {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0.5rem;
        }
        .avatar-preview {
            width: 96px;
            height: 96px;
            border-radius: 50%;
            object-fit: cover;
            border: 2px solid #dee2e6;
        }
        .avatar-placeholder {
            width: 96px;
            height: 96px;
            border-radius: 50%;
            background: #e9ecef;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 2.5rem;
            color: #adb5bd;
            border: 2px solid #dee2e6;
        }
        .avatar-input { display: none; }
    `;

    constructor() {
        super();
        this.email = sessionStorage.getItem('d3auth_setup_email') || '';
        this.setupToken = sessionStorage.getItem('d3auth_setup_token') || '';
        this.username = '';
        this.avatarPreview = null;
        this.avatarFile = null;
        this.error = '';
        this.usernameStatus = '';
        this.loading = false;
        this._usernameTimer = null;
    }

    async connectedCallback() {
        super.connectedCallback();
        if (!this.email) {
            router.navigate('/');
        }
    }

    _handleUsernameInput(e) {
        this.username = e.target.value;
        this.usernameStatus = '';

        if (this._usernameTimer) clearTimeout(this._usernameTimer);

        if (this.username.length >= 3) {
            this._usernameTimer = setTimeout(() => this._checkUsername(), 500);
        }
    }

    async _checkUsername() {
        try {
            const response = await fetch(`${getBackendUrl()}/api/v1/check-username`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: this.username }),
            });
            const data = await response.json();
            this.usernameStatus = data.available
                ? 'available'
                : data.error || 'Username not available';
        } catch {
            this.usernameStatus = '';
        }
    }

    _handleAvatarClick() {
        /** @type {HTMLElement | null} */
        const input = this.shadowRoot.querySelector('.avatar-input');
        input?.click();
    }

    _handleAvatarChange(e) {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            this.error = 'Please select an image file';
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            this.error = 'Image must be under 2MB';
            return;
        }

        this.avatarFile = file;
        this.avatarPreview = URL.createObjectURL(file);
        this.error = '';
    }

    async _handleSubmit(e) {
        e.preventDefault();
        this.error = '';

        if (this.username.length < 3) {
            this.error = 'Username must be at least 3 characters';
            return;
        }

        this.loading = true;

        try {
            // Create the account
            const response = await fetch(`${getBackendUrl()}/api/v1/complete-setup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: this.username,
                    setup_token: this.setupToken,
                }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Setup failed');
            }

            // Sign in with the new tokens
            authState.setTokens(data.tokens);
            authState.isAuthenticated = true;

            // Upload avatar if selected
            if (this.avatarFile) {
                const formData = new FormData();
                formData.append('avatar', this.avatarFile);

                const token = authState.getAccessToken();
                await fetch(`${getBackendUrl()}/api/v1/avatar`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                    body: formData,
                });
            }

            // Clean up
            sessionStorage.removeItem('d3auth_setup_email');
            sessionStorage.removeItem('d3auth_setup_name');
            sessionStorage.removeItem('d3auth_setup_token');

            await authState.fetchCurrentUser();
            authState.notify();
            // A new account often starts from an app's sign-in, and setup is the
            // detour on the way — resume the app's authorize request if one waits.
            navigateAfterSignIn(sessionStorage, authState.consentsOutstanding);
        } catch (err) {
            this.error = err.message;
        } finally {
            this.loading = false;
        }
    }

    render() {
        return html`
            <div class="setup-card mt-5">
                <div class="card shadow">
                    <div class="card-body p-4">
                        <h3 class="card-title text-center mb-2">Welcome!</h3>
                        <p class="text-muted text-center mb-4">Set up your profile to get started.</p>

                        ${this.error ? html`<div class="alert alert-danger d-flex align-items-start gap-2"><i class="bi bi-exclamation-triangle-fill" style="font-size: 1.25rem; margin-top: 0.1rem;"></i> <span>${this.error}</span></div>` : ''}

                        <form @submit="${this._handleSubmit}">
                            <div class="mb-4 avatar-upload">
                                ${
                                    this.avatarPreview
                                        ? html`<img src="${this.avatarPreview}" class="avatar-preview" alt="Avatar">`
                                        : html`<div class="avatar-placeholder"><i class="bi bi-person"></i></div>`
                                }
                                <button type="button" class="btn btn-outline-secondary btn-sm" @click="${this._handleAvatarClick}">
                                    ${this.avatarPreview ? 'Change photo' : 'Upload photo'}
                                </button>
                                <input type="file" class="avatar-input" accept="image/*" @change="${this._handleAvatarChange}">
                                <small class="text-muted">Optional. Max 2MB.</small>
                            </div>

                            <div class="mb-3">
                                <label class="form-label text-muted">Email</label>
                                <div>${this.email}</div>
                            </div>

                            <div class="mb-3">
                                <label for="username" class="form-label">Username</label>
                                <input type="text" class="form-control" id="username" required minlength="3"
                                    placeholder="Choose a username"
                                    .value="${this.username}"
                                    @input="${this._handleUsernameInput}">
                                ${
                                    this.usernameStatus === 'available'
                                        ? html`<div class="form-text text-success"><i class="bi bi-check-circle"></i> Username is available</div>`
                                        : this.usernameStatus
                                          ? html`<div class="form-text text-danger"><i class="bi bi-x-circle"></i> ${this.usernameStatus}</div>`
                                          : html`<div class="form-text">Letters, digits, hyphens, underscores, periods. At least 3 characters.</div>`
                                }
                            </div>

                            <button type="submit" class="btn btn-primary w-100" ?disabled="${this.loading || this.usernameStatus !== 'available'}">
                                ${this.loading ? 'Creating account...' : 'Continue'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        `;
    }
}

customElements.define('setup-view', SetupView);
export { SetupView };
